import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview StatusBadge Component
 *
 * Badge component for displaying status indicators with semantic colors and icons.
 * Supports success, warning, error, and info states with consistent styling.
 *
 * @author LOCI Development Team
 * @since 1.1.0
 */
import { forwardRef } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
/**
 * Icon mapping for status types
 */
const STATUS_ICONS = {
    success: CheckCircle,
    warning: AlertTriangle,
    error: XCircle,
    info: Info,
};
/**
 * Size configurations for StatusBadge
 */
const SIZE_CONFIG = {
    xs: { height: 'h-5', padding: 'px-1.5', fontSize: 'text-xs', gap: 'gap-0.5' },
    sm: { height: 'h-6', padding: 'px-2', fontSize: 'text-xs', gap: 'gap-1' },
    md: { height: 'h-7', padding: 'px-2.5', fontSize: 'text-sm', gap: 'gap-1' },
    lg: { height: 'h-8', padding: 'px-3', fontSize: 'text-base', gap: 'gap-1.5' },
};
/**
 * Icon size mapping
 */
const ICON_SIZE_MAP = {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18,
};
/**
 * Status color configurations using design tokens
 * Hoisted to module scope to avoid recreation on every render
 */
const STATUS_COLORS = {
    success: {
        bg: 'bg-green-50',
        text: 'text-green-900',
        border: 'border-green-200',
        icon: 'text-green-700',
    },
    warning: {
        bg: 'bg-yellow-50',
        text: 'text-yellow-900',
        border: 'border-yellow-200',
        icon: 'text-yellow-700',
    },
    error: {
        bg: 'bg-red-50',
        text: 'text-red-900',
        border: 'border-red-200',
        icon: 'text-red-700',
    },
    info: {
        bg: 'bg-blue-50',
        text: 'text-blue-900',
        border: 'border-blue-200',
        icon: 'text-blue-700',
    },
};
/**
 * StatusBadge Component
 *
 * Displays status indicators with semantic colors, icons, and accessibility features.
 */
export const StatusBadge = forwardRef(({ status, label, size = 'md', variant = 'subtle', ariaLive = 'polite', className = '', 'data-testid': testId, 'aria-label': ariaLabel, }, ref) => {
    const sizeConfig = SIZE_CONFIG[size];
    const IconComponent = STATUS_ICONS[status];
    const iconSize = ICON_SIZE_MAP[size];
    const statusColors = STATUS_COLORS[status];
    const classes = [
        'inline-flex items-center justify-center',
        'font-medium rounded-full',
        'border transition-colors duration-200 motion-safe:transition-colors',
        statusColors.bg,
        statusColors.text,
        statusColors.border,
        sizeConfig.height,
        sizeConfig.padding,
        sizeConfig.fontSize,
        sizeConfig.gap,
        className,
    ]
        .filter(Boolean)
        .join(' ');
    return (_jsxs("span", { ref: ref, className: classes, "data-testid": testId || `status-badge-${status}`, role: "status", "aria-live": ariaLive, "aria-label": ariaLabel || `${status} status: ${label}`, children: [_jsx(IconComponent, { size: iconSize, className: `${statusColors.icon} flex-shrink-0`, "aria-hidden": "true" }), _jsx("span", { className: "whitespace-nowrap select-none", children: label })] }));
});
StatusBadge.displayName = 'StatusBadge';
